import React, { useMemo } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useGetCustomerEngagementQuery, useGetCustomerEventStatsQuery } from "../../../app/api/console/consoleCustomerAnalyticsApiSlice";
import { Card, CardHeader, Donut, EmptyState, Loading, Reading, StatCard } from "./ui";
import { actionsByPosition, discardReasons, lifecycleMix, rangeToIso, shareTargets } from "./helpers";

const DAILY_SERIES = [
  ["mark_done", "Marks done", "#1FAF5C"],
  ["discard", "Discards", "#FF7B43"],
  ["assign", "Claims", "#8B5CF6"],
  ["drag_reorder", "Drag", "#EC4899"],
  ["share", "Shares", "#6C7187"],
];

// What logged-in users do with findings: live counts from the Platform /stats aggregator.
export default function ActionsTab({ timeRange, useProd }) {
  const { data: stats, isLoading, error } = useGetCustomerEventStatsQuery({ timeRange, useProd });
  const window = useMemo(() => rangeToIso(timeRange), [timeRange]);
  const { data: engagement } = useGetCustomerEngagementQuery({ start: window.start, end: window.end, useProd });
  const daily = (engagement && engagement.daily) || [];
  const agents = (engagement && engagement.agents) || [];
  const mix = useMemo(() => lifecycleMix(stats), [stats]);
  const byPosition = useMemo(() => actionsByPosition(stats), [stats]);
  const reasons = useMemo(() => discardReasons(stats), [stats]);
  const shares = useMemo(() => shareTargets(stats), [stats]);

  if (isLoading) return <Loading label='Loading action stats…' />;
  if (error) return <EmptyState title='Action stats unavailable'>The Platform stats endpoint returned an error for this environment.</EmptyState>;

  const total = (stats && stats.total_actions) || 0;
  const pageViews = (stats && stats.page_views) || 0;
  const external = (stats && stats.external_clicks) || 0;

  return (
    <div className='space-y-6' data-testid='tab-actions'>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3.5'>
        <StatCard label='Events captured' value={total.toLocaleString()} note={`all telemetry events · ${timeRange}`} />
        <StatCard label='Marks done' value={mix.data[0].value} note='finding.marked_done' color='text-accent-green' labelColor='text-accent-green' />
        <StatCard label='Discards' value={mix.data[1].value} note='finding.discarded' color='text-severity-high' labelColor='text-severity-high' />
        <StatCard label='Claims' value={mix.data[2].value} note='finding.assigned' color='text-accent-purple' labelColor='text-accent-purple' />
        <StatCard label='Drag reorders' value={mix.data[3].value} note='finding.drag_reordered' color='text-pink-600' labelColor='text-pink-600' />
        <StatCard label='Page views' value={pageViews} note={`${external} external link clicks`} />
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <Card className='flex flex-col justify-between'>
          <div>
            <CardHeader
              title='Lifecycle action mix'
              badge={`${mix.total} actions · ${timeRange}`}
              hint='How users dispose of findings: marks done, discards, shares, claims, drag-reorders. Tells us whether users are fixing things or just filtering them.'
            />
            {mix.total === 0 ? <p className='text-text-tertiary text-body-xs'>No finding actions in this window.</p> : <Donut data={mix.data} total={mix.total} unit='actions' />}
          </div>
          {mix.total > 0 && (
            <Reading>
              {mix.data[0].percentage + mix.data[1].percentage}% of decisions are "fix or filter" (done + discard); {mix.data[4].percentage}% get shared externally
              {shares.length > 0 ? ` (${shares.map((s) => `${s.target} ${s.count}`).join(", ")})` : ""}.
            </Reading>
          )}
        </Card>

        <Card className='flex flex-col justify-between'>
          <div>
            <CardHeader
              title='Actions by ranking position'
              badge='action_by_position'
              hint='How many actions (done, discard, share) landed on each top-10 position. A steep decline means the ranker is differentiating items, not just shuffling.'
            />
            {byPosition.length === 0 ? (
              <p className='text-text-tertiary text-body-xs'>No positioned actions in this window.</p>
            ) : (
              <div className='h-52'>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart data={byPosition}>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} />
                    <XAxis dataKey='position' tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip formatter={(v) => [v, "actions"]} labelFormatter={(l) => `Position ${l}`} />
                    <Bar dataKey='count' fill='#1FAF5C' opacity={0.85} radius={[3, 3, 0, 0]} name='Actions' />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader title='Discard reasons' badge={`${reasons.reduce((s, r) => s + r.count, 0)} discards`} hint='Reason picked on every discard. An imbalance points at a copy or prompt fix; the Issue Analytics page groups these into precision / value / noise.' />
        {reasons.length === 0 ? (
          <p className='text-text-tertiary text-body-xs'>No discards with a reason in this window.</p>
        ) : (
          <div className='h-52'>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart data={reasons}>
                <CartesianGrid strokeDasharray='3 3' vertical={false} />
                <XAxis dataKey='reason' tick={{ fontSize: 10 }} />
                <YAxis unit='%' tick={{ fontSize: 11 }} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload || !payload.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className='bg-white border border-border-subtle rounded-xl shadow-lg p-3 text-caption'>
                        <p className='font-bold text-text-primary'>{d.reason}</p>
                        <p className='mt-1 font-semibold text-accent-purple'>Discards: {d.count}</p>
                        <p className='font-semibold text-text-secondary'>Share: {d.percentage}%</p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey='percentage' fill='#8B5CF6' opacity={0.8} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card className='space-y-4'>
        <CardHeader
          title='Actions over time'
          badge={
            <span className='flex flex-wrap gap-x-3 font-semibold uppercase tracking-wider'>
              {DAILY_SERIES.map(([k, label, color]) => (
                <span key={k} className='flex items-center gap-1.5'><span className='w-2.5 h-2.5 rounded-xs' style={{ backgroundColor: color }} /> {label}</span>
              ))}
            </span>
          }
          hint='Finding actions per UTC day (marks done, discards, claims, drag-reorders, shares).'
        />
        {daily.length === 0 ? (
          <p className='text-text-tertiary text-body-xs'>No finding actions in this window.</p>
        ) : (
          <div className='h-52'>
            <ResponsiveContainer width='100%' height='100%'>
              <AreaChart data={daily}>
                <CartesianGrid strokeDasharray='3 3' vertical={false} />
                <XAxis dataKey='date' tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                {DAILY_SERIES.map(([k, label, color]) => (
                  <Area key={k} type='monotone' dataKey={k} name={label} stackId='1' stroke={color} fill={color} opacity={0.7} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title='Discards vs marks done by agent type' badge='agent_type on each action' hint='Share of decisions that were discards, per agent type the Hub reported on the action. Issue Analytics has the deeper view (reject rate over every issue surfaced, by agent family).' />
        {agents.length === 0 ? (
          <p className='text-text-tertiary text-body-xs'>No decisions with an agent type in this window.</p>
        ) : (
          <div className='space-y-3 my-2'>
            {agents.map((a) => {
              const rate = a.discard_rate == null ? 0 : a.discard_rate;
              const tone = rate < 0.15 ? "bg-accent-green" : rate < 0.3 ? "bg-accent-purple" : rate < 0.45 ? "bg-amber-500" : "bg-red-600";
              return (
                <div key={a.agent_type} className='space-y-1'>
                  <div className='flex items-center justify-between text-body-xs font-bold'>
                    <span className='text-text-primary text-[11px]'>{a.agent_type}</span>
                    <span className='text-text-secondary'>
                      {a.discard_rate == null ? "—" : `${Math.round(rate * 100)}%`} <span className='text-[10px] font-normal text-text-tertiary'>({a.discarded} discarded · {a.done} done)</span>
                    </span>
                  </div>
                  <div className='w-full bg-gray-100 rounded-full h-3 overflow-hidden'>
                    <div className={`${tone} h-full`} style={{ width: `${Math.round(rate * 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className='text-caption text-text-tertiary mt-3'>Severity overrides are not instrumented: there is no finding.severity_changed event yet.</p>
      </Card>
    </div>
  );
}
